// src/components/Admin/DonorDetailsModal.tsx

import React from "react";
import {
  Box,
  Typography,
  Grid,
  Divider,
  Card,
  Avatar,
  useTheme,
  alpha,
  Chip,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
} from "@mui/material";

// Icons
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailIcon from "@mui/icons-material/Email";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import RedeemIcon from "@mui/icons-material/Redeem";
import SyncIcon from "@mui/icons-material/Sync";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PhoneIcon from "@mui/icons-material/Phone";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import EditIcon from "@mui/icons-material/Edit";

import CustomTable from "../Common/Table";
import Action from "../Common/Action";
import CustomModal from "../Common/Modal";
import type { Donation, EnhancedDonor, DonationStatus } from "../../../types/donation";

interface DonorDetailsModalProps {
  open: boolean;
  donor: EnhancedDonor;
  donations: Donation[];
  loading: boolean;
  onClose: () => void;
  onUpdateStatus: (
    donationId: string,
    currentStatus: DonationStatus,
    donation: Donation
  ) => void;
}

const statusColors: Record<string, { color: string; backgroundColor: string }> = {
  pending: { color: "#ff9800", backgroundColor: alpha("#ff9800", 0.1) },
  processing: { color: "#2196f3", backgroundColor: alpha("#2196f3", 0.1) },
  completed: { color: "#4caf50", backgroundColor: alpha("#4caf50", 0.1) },
  failed: { color: "#f44336", backgroundColor: alpha("#f44336", 0.1) },
  refunded: { color: "#9c27b0", backgroundColor: alpha("#9c27b0", 0.1) },
  cancelled: { color: "#607d8b", backgroundColor: alpha("#607d8b", 0.1) },
};

const DonorDetailsModal: React.FC<DonorDetailsModalProps> = ({
  open,
  donor,
  donations,
  loading,
  onClose,
  onUpdateStatus,
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = React.useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const columns = [
    {
      label: "Amount",
      key: "amount" as keyof Donation,
      format: (value: number) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography 
            sx={{ 
              fontWeight: 600, 
              color: theme.palette.success.main 
            }}
          >
            ${value.toFixed(2)}
          </Typography>
        </Box>
      ),
    },
    {
      label: "Status",
      key: "status" as keyof Donation,
      format: (value: string) => {
        const statusColor = statusColors[value] || { color: theme.palette.grey[700], backgroundColor: theme.palette.grey[200] };
        return (
          <Chip
            label={value.charAt(0).toUpperCase() + value.slice(1)}
            sx={{
              fontWeight: 500,
              color: statusColor.color,
              backgroundColor: statusColor.backgroundColor,
            }}
            size="small"
          />
        );
      }
    },
    {
      label: "Date",
      key: "createdAt" as keyof Donation,
      format: (value: string) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CalendarTodayIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.text.secondary }} />
          <Typography variant="body2">
            {new Date(value).toLocaleDateString()}
          </Typography>
        </Box>
      ),
    },
    {
      label: "Frequency",
      key: "frequency" as keyof Donation,
      format: (value: string) => {
        const isRecurring = value === 'recurring';
        return (
          <Chip
            label={isRecurring ? 'Recurring' : 'One-time'}
            color={isRecurring ? 'primary' : 'default'}
            variant={isRecurring ? 'filled' : 'outlined'}
            size="small"
          />
        );
      }
    },
    {
      label: "Currency",
      key: "currency" as keyof Donation,
      format: (value: string) => (
        <Chip
          label={value.toUpperCase()}
          variant="outlined"
          size="small"
          sx={{ minWidth: 60, fontWeight: 500 }}
        />
      ),
    },
  ];

  // Calculate total and average donation amounts
  const totalDonationAmount = donations.reduce((sum, donation) => sum + (donation.amount || 0), 0);
  const avgDonationAmount = donations.length > 0 ? totalDonationAmount / donations.length : 0;

  return (
    <CustomModal 
      open={open} 
      title={`Donor Details`} 
      onClose={onClose} 
      maxWidth="md"
      fullWidth
    >
      <Box mb={2}>
        <IconButton 
          onClick={onClose} 
          sx={{ 
            mb: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.2),
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>

      <Card 
        sx={{ 
          p: 0, 
          overflow: 'hidden',
          borderRadius: 3,
          boxShadow: theme.shadows[2],
          mb: 4
        }}
      >
        {/* Profile Header with background and Avatar */}
        <Box 
          sx={{
            position: 'relative',
            height: 120,
            bgcolor: alpha(theme.palette.primary.main, 0.8),
            mb: 5,
          }}
        >
          <Avatar
            sx={{
              bgcolor: theme.palette.primary.main,
              width: 90,
              height: 90,
              position: 'absolute',
              bottom: -40,
              left: 30,
              border: `4px solid ${theme.palette.background.paper}`,
              boxShadow: theme.shadows[3],
            }}
          >
            <PersonOutlineIcon sx={{ fontSize: 40 }} />
          </Avatar>
        </Box>

        <Box px={3} pb={3}>
          <Box ml={14}>
            <Typography variant="h5" fontWeight="700" gutterBottom>
              {donor.name || "No Name"}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" mb={1}>
              <Chip 
                label={`${donor.donationCount} Donations`} 
                icon={<ReceiptLongIcon />} 
                color="primary" 
                variant="outlined"
                size="small"
              />
              <Chip 
                label={`$${donor.totalDonations.toLocaleString()}`} 
                icon={<VolunteerActivismIcon />} 
                color="success" 
                variant="outlined"
                size="small"
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Donor ID: {donor.id}
            </Typography>
          </Box>
        </Box>
      </Card>

      <Box sx={{ mb: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: '0.9rem',
              py: 2
            },
            borderBottom: `1px solid ${theme.palette.divider}`,
            mb: 3
          }}
        >
          <Tab icon={<PersonOutlineIcon />} label="Donor Info" iconPosition="start" />
          <Tab icon={<ReceiptLongIcon />} label="Donation History" iconPosition="start" />
        </Tabs>

        {activeTab === 0 && (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <Box p={3}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                    Contact Information
                  </Typography>
                  
                  <Stack spacing={2}>
                    {/* Email */}
                    <Box display="flex" alignItems="center">
                      <Avatar 
                        sx={{ 
                          width: 36, 
                          height: 36, 
                          bgcolor: alpha(theme.palette.primary.main, 0.1), 
                          color: theme.palette.primary.main,
                          mr: 2
                        }}
                      >
                        <EmailIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body2">
                          {donor.email || "No Email"}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Phone - placeholder for future data */}
                    <Box display="flex" alignItems="center">
                      <Avatar 
                        sx={{ 
                          width: 36, 
                          height: 36, 
                          bgcolor: alpha(theme.palette.grey[500], 0.1), 
                          color: theme.palette.grey[600],
                          mr: 2
                        }}
                      >
                        <PhoneIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {donor.phone || "Not provided"}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Address - placeholder for future data */}
                    <Box display="flex" alignItems="center">
                      <Avatar 
                        sx={{ 
                          width: 36, 
                          height: 36, 
                          bgcolor: alpha(theme.palette.grey[500], 0.1), 
                          color: theme.palette.grey[600],
                          mr: 2
                        }}
                      >
                        <LocationOnIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Address
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {donor.address || "Not provided"}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                    Donation Summary
                  </Typography>
                  
                  <Stack spacing={2}>
                    {/* Total Donations */}
                    <Box display="flex" alignItems="center">
                      <Avatar 
                        sx={{ 
                          width: 36, 
                          height: 36, 
                          bgcolor: alpha(theme.palette.success.main, 0.1), 
                          color: theme.palette.success.main,
                          mr: 2
                        }}
                      >
                        <AttachMoneyIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Total Donations
                        </Typography>
                        <Typography variant="body1" fontWeight="600" color="success.main">
                          ${donor.totalDonations.toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Donation Count */}
                    <Box display="flex" alignItems="center">
                      <Avatar 
                        sx={{ 
                          width: 36, 
                          height: 36, 
                          bgcolor: alpha(theme.palette.primary.main, 0.1), 
                          color: theme.palette.primary.main,
                          mr: 2
                        }}
                      >
                        <RedeemIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Donation Count
                        </Typography>
                        <Typography variant="body1" fontWeight="600">
                          {donor.donationCount}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Average Donation */}
                    <Box display="flex" alignItems="center">
                      <Avatar 
                        sx={{ 
                          width: 36, 
                          height: 36, 
                          bgcolor: alpha(theme.palette.info.main, 0.1), 
                          color: theme.palette.info.main,
                          mr: 2
                        }}
                      >
                        <AttachMoneyIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Average Donation
                        </Typography>
                        <Typography variant="body1" fontWeight="600" color="info.main">
                          ${avgDonationAmount.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
              
              <Box mt={3} display="flex" justifyContent="flex-end">
                <Button 
                  variant="outlined" 
                  color="primary" 
                  startIcon={<EditIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Edit Donor Information
                </Button>
              </Box>
            </Box>
          </Card>
        )}

        {activeTab === 1 && (
          <>
            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
              Donation History
            </Typography>
            <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              {donations.length > 0 ? (
                <CustomTable<Donation>
                  columns={columns}
                  data={donations}
                  loading={loading}
                  page={0}
                  rowsPerPage={donations.length}
                  onPageChange={() => {}}
                  onRowsPerPageChange={() => {}}
                  rowsPerPageOptions={[]}
                  rowActions={(donation) => (
                    <Action
                      actions={[
                        {
                          label: "Update Status",
                          icon: <SyncIcon fontSize="small" />,
                          color: theme.palette.primary.main,
                          onClick: () =>
                            onUpdateStatus(donation.id, donation.status, donation),
                        },
                      ]}
                    />
                  )}
                />
              ) : (
                <Box p={3} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    No donations found for this donor.
                  </Typography>
                </Box>
              )}
            </Card>
          </>
        )}
      </Box>
    </CustomModal>
  );
};

export default DonorDetailsModal;
